import { Component, Input, OnInit } from '@angular/core';
import { DashboardModel, DashboardModelDefaultValues } from '../dashboard-model';
import { DashboardAnalyticsModel } from '../dashboard-analytics-model';
import { DashboardService } from 'src/app/services/dashboard.service';
import { AuthService } from 'src/app/services/auth.service';
import { AdminProgramMode } from '../../authorize/admin-program-mode';

interface StatMetric {
  label: string;
  icon: string;
  accent: 'teal' | 'blue' | 'purple' | 'amber' | 'green';
  program?: AdminProgramMode | 'both';
  getValue: (analytics: DashboardAnalyticsModel) => number;
}

interface InvitationMetric {
  label: string;
  icon: string;
  tone: 'default' | 'warning' | 'danger';
  getValue: () => number;
}

@Component({
    selector: 'app-dashboard-devadmin',
    templateUrl: './dashboard-devadmin.component.html',
    styleUrls: ['./dashboard-devadmin.component.scss'],
    standalone: false
})
export class DashboardDevadminComponent implements OnInit {
  @Input() dashboardModel: DashboardModel = DashboardModelDefaultValues();

  startDate: Date = new Date();
  endDate: Date = new Date();
  coachedParticipantCount = 0;
  drpCoachParticipantValue = '1';
  programMode: AdminProgramMode = 'bb';

  readonly periodOptions = [
    { value: '1', label: 'Last 30 days' },
    { value: '2', label: 'Last 3 months' },
    { value: '3', label: 'Last year' }
  ];

  private readonly allPlatformMetrics: StatMetric[] = [
    { label: 'Active Admins', icon: 'admin_panel_settings', accent: 'blue', program: 'bb', getValue: (a) => a.bbActiveAdminUsers },
    { label: 'Active Admins', icon: 'admin_panel_settings', accent: 'blue', program: 'llttf', getValue: (a) => a.llttfActiveAdminUsers },
    { label: 'BB Coached', icon: 'support_agent', accent: 'green', program: 'bb', getValue: (a) => a.bbCoachedParticipants },
    { label: 'BB Self-Guided', icon: 'menu_book', accent: 'teal', program: 'bb', getValue: (a) => a.bbSelfGuidedParticipants },
    { label: 'LLTTF Participants', icon: 'school', accent: 'purple', program: 'llttf', getValue: (a) => a.llttfParticipants },
    { label: 'Organizations', icon: 'domain', accent: 'blue', program: 'llttf', getValue: (a) => a.organizations },
    { label: 'Courses', icon: 'library_books', accent: 'amber', program: 'bb', getValue: (a) => a.courses },
    { label: 'Materials', icon: 'description', accent: 'amber', program: 'bb', getValue: (a) => a.materials }
  ];

  platformMetrics: StatMetric[] = [];
  bbInvitationMetrics: InvitationMetric[] = [];
  llttfInvitationMetrics: InvitationMetric[] = [];

  constructor(
    private _dashboardService: DashboardService,
    private _authService: AuthService) {}

  ngOnInit(): void {
    this.programMode = this._authService.getEffectiveProgramMode();
    this.platformMetrics = this.allPlatformMetrics.filter(metric =>
      metric.program === 'both' || metric.program === this.programMode);

    this.buildInvitationMetrics();
    if (this.showBbGrowth) {
      this.applySelectedPeriod();
    }
  }

  /** BB invitation cards: BB mode (dual toggle or BB-only effective mode). */
  get showBbInvitations(): boolean {
    return this.programMode === 'bb';
  }

  /** LLTTF invitation cards: LLTTF mode (dual toggle or LLTTF-only effective mode). */
  get showLlttfInvitations(): boolean {
    return this.programMode === 'llttf';
  }

  get showBbGrowth(): boolean {
    return this.programMode === 'bb';
  }

  get showGrowthEmptyHint(): boolean {
    return this.coachedParticipantCount === 0;
  }

  setPeriod(value: string): void {
    if (this.drpCoachParticipantValue === value) {
      return;
    }
    this.drpCoachParticipantValue = value;
    this.applySelectedPeriod();
  }

  private buildInvitationMetrics(): void {
    this.bbInvitationMetrics = [
      {
        label: 'Not accepted',
        icon: 'mail',
        tone: 'default',
        getValue: () => this.dashboardModel.invitationsNotAcceptedCount
      },
      {
        label: 'Expiring soon',
        icon: 'schedule',
        tone: 'warning',
        getValue: () => this.dashboardModel.invitationsExpiringCount
      },
      {
        label: 'Expired',
        icon: 'event_busy',
        tone: 'danger',
        getValue: () => this.dashboardModel.invitationsExpiredCount
      }
    ];

    this.llttfInvitationMetrics = [
      {
        label: 'Active cohorts',
        icon: 'diversity_3',
        tone: 'default',
        getValue: () => this.dashboardModel.analytics.llttfInvitationsNotAcceptedCount
      },
      {
        label: 'Expiring soon',
        icon: 'schedule',
        tone: 'warning',
        getValue: () => this.dashboardModel.analytics.llttfInvitationsExpiringCount
      },
      {
        label: 'Expired',
        icon: 'event_busy',
        tone: 'danger',
        getValue: () => this.dashboardModel.analytics.llttfInvitationsExpiredCount
      }
    ];
  }

  /** Sets start/end from the selected period, then loads the count once. */
  private applySelectedPeriod(): void {
    const startDate = new Date();
    switch (this.drpCoachParticipantValue) {
      case '1':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '2':
        startDate.setDate(startDate.getDate() - 90);
        break;
      case '3':
        startDate.setDate(startDate.getDate() - 365);
        break;
      default:
        startDate.setDate(startDate.getDate() - 30);
        break;
    }
    this.startDate = startDate;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.endDate = tomorrow;

    this._dashboardService
      .getCoachedParticipantCountForCoachAndAdmin(this.startDate, this.endDate)
      .subscribe({
        next: (data) => {
          this.coachedParticipantCount = data;
        }
      });
  }
}
